import { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface AdminAuthState {
  isLoggedIn: boolean;
  username: string;
}

interface AdminAuthContextType {
  auth: AdminAuthState;
  login: (username: string, password: string) => boolean;
  logout: () => void;
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const ADMIN_CREDENTIALS = {
  username: 'admin',
  password: 'FMOadmin2026!',
};

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState<AdminAuthState>({ isLoggedIn: false, username: '' });
  const [isOpen, setIsOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('fmo_admin_auth');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.isLoggedIn && parsed.username) {
          setAuth(parsed);
        }
      } catch { /* ignore */ }
    }
    setInitialized(true);
  }, []);

  const login = useCallback((username: string, password: string): boolean => {
    if (username === ADMIN_CREDENTIALS.username && password === ADMIN_CREDENTIALS.password) {
      const newAuth = { isLoggedIn: true, username };
      setAuth(newAuth);
      localStorage.setItem('fmo_admin_auth', JSON.stringify(newAuth));
      setIsOpen(false);
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    setAuth({ isLoggedIn: false, username: '' });
    localStorage.removeItem('fmo_admin_auth');
  }, []);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  if (!initialized) return null;

  return (
    <AdminAuthContext.Provider value={{ auth, login, logout, isOpen, openModal, closeModal }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}